/**
 * Pestañas accesibles simples (sin biblioteca de componentes): `role="tablist"` +
 * `aria-selected`, navegación con flechas izquierda/derecha entre pestañas (docs/FASE-1.md §6
 * "Accesibilidad mínima"). El contenido de la pestaña activa se pasa como `children`; el
 * llamador decide qué renderizar según `active` (útil para no montar pestañas no visitadas).
 */
import { useId, useState, type KeyboardEvent, type ReactElement } from "react";

export interface TabItem {
  key: string;
  label: string;
}

export function Tabs({
  items,
  active,
  onChange,
}: {
  items: readonly TabItem[];
  active: string;
  onChange: (key: string) => void;
}): ReactElement {
  const baseId = useId();

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number): void {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    event.preventDefault();
    const delta = event.key === "ArrowRight" ? 1 : -1;
    const nextIndex = (index + delta + items.length) % items.length;
    const next = items[nextIndex];
    if (next) onChange(next.key);
  }

  return (
    <div className="tabs" role="tablist">
      {items.map((item, index) => (
        <button
          key={item.key}
          id={`${baseId}-tab-${item.key}`}
          type="button"
          role="tab"
          aria-selected={item.key === active}
          className={item.key === active ? "tab tab-active" : "tab"}
          onClick={() => onChange(item.key)}
          onKeyDown={(event) => handleKeyDown(event, index)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

/** Hook mínimo para manejar la pestaña activa con un valor por defecto. */
export function useTabs(defaultKey: string): [string, (key: string) => void] {
  const [active, setActive] = useState(defaultKey);
  return [active, setActive];
}
